import { Alert, Collapse, Skeleton, Table, Tag, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useEffect, useMemo, useState } from "react"

import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { getEntityName } from "../../../../utils/user/get-entity-name.ts"
import { selectAccessibleBranches } from "../../../Branches/redux/branches.slice.ts"
import { getUsers } from "../../api/services.ts"
import type { TeamMembersFilters } from "../../redux/team-members.types.ts"
import type { TeamMember, TeamMemberFormIntent } from "../../types/types.ts"

const { Text } = Typography

// Companies rarely run into the thousands of employees, and this view needs
// every one of them at once to build an accurate hierarchy — a page this
// size, fetched once per filter change, is simpler and safer than teaching
// a second view to paginate a tree.
const ORGANIZATION_PAGE_SIZE = 500

// A member counts as "company-level leadership" for this view if they hold
// all-current-and-future-branches access, or the reserved HQ/ownership
// labels ("gcc"/"gcc_admin") on any of their own branches — the same two
// signals apps.user.services.branch_access_service treats as the
// HQ/ownership tier everywhere else this session.
const RESERVED_LEADERSHIP_CODES = new Set(["gcc", "gcc_admin"])

function isCompanyLevel(member: TeamMember): boolean {
  if (member.company_wide_access) return true
  return (member.branches || []).some(
    (b) => b.role_code && RESERVED_LEADERSHIP_CODES.has(b.role_code)
  )
}

interface TeamOrganizationViewProps {
  filters: TeamMembersFilters
  onSelectUser: (user: TeamMember, intent?: TeamMemberFormIntent) => void
}

export function TeamOrganizationView({
  filters,
  onSelectUser
}: TeamOrganizationViewProps) {
  const accessibleBranches = useAppSelector(selectAccessibleBranches)
  const [loading, setLoading] = useState(false)
  const [members, setMembers] = useState<TeamMember[]>([])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getUsers({
      q: filters?.q,
      labels: filters?.labels,
      status: filters?.status,
      team_ids: filters?.team_ids,
      page_size: ORGANIZATION_PAGE_SIZE,
      page: 1
    })
      .then((response) => {
        if (cancelled) return
        if (response.ok) setMembers(response.data?.results || [])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [filters?.q, filters?.labels, filters?.status, filters?.team_ids])

  const uniqueMemberCount = members.length

  const companyLevelMembers = useMemo(
    () => members.filter(isCompanyLevel),
    [members]
  )

  const branchGroups = useMemo(() => {
    const visibleBranches = filters?.team_ids
      ? accessibleBranches.filter((b) =>
          filters.team_ids?.split(",").includes(b.id)
        )
      : accessibleBranches
    return visibleBranches.map((branch) => ({
      branch,
      members: members.filter((m) =>
        (m.branches || []).some((b) => b.team_id === branch.id)
      )
    }))
  }, [accessibleBranches, members, filters?.team_ids])

  const memberColumns = (branchId: string): ColumnsType<TeamMember> => [
    {
      title: "Name",
      key: "name",
      render: (_, member) => getEntityName(member)
    },
    {
      title: "Role in this branch",
      key: "role",
      render: (_, member) => {
        const row = (member.branches || []).find((b) => b.team_id === branchId)
        return row?.role_name || "—"
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status"
    },
    {
      title: "Other branches",
      key: "other-branches",
      render: (_, member) =>
        member.is_shared_across_branches ? (
          <Tag color="blue">Shared across branches</Tag>
        ) : (
          "—"
        )
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, member) => <a onClick={() => onSelectUser(member)}>Edit</a>
    }
  ]

  if (loading && !members.length) {
    return <Skeleton active paragraph={{ rows: 6 }} />
  }

  return (
    <div className="flex flex-col gap-3">
      <Alert
        type="info"
        showIcon
        message={`${uniqueMemberCount} unique company member${uniqueMemberCount === 1 ? "" : "s"}`}
        description="Counts each person once, however many branches they belong to."
      />

      {companyLevelMembers.length > 0 && (
        <div className="border rounded-md p-3">
          <Text strong className="block mb-2">
            Company HQ — Leadership &amp; company-wide access
          </Text>
          <Table<TeamMember>
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={companyLevelMembers}
            columns={[
              {
                title: "Name",
                key: "name",
                render: (_, m) => getEntityName(m)
              },
              {
                title: "Access",
                key: "access",
                render: (_, m) =>
                  m.company_wide_access ? (
                    <Tag color="purple">{m.company_wide_access.role_name}</Tag>
                  ) : (
                    m.label_name || "—"
                  )
              },
              { title: "Status", dataIndex: "status", key: "status" },
              {
                title: "Actions",
                key: "actions",
                render: (_, m) => <a onClick={() => onSelectUser(m)}>Edit</a>
              }
            ]}
          />
        </div>
      )}

      <Collapse
        items={branchGroups.map(({ branch, members: branchMembers }) => ({
          key: branch.id,
          label: (
            <span>
              <Text strong>{branch.name}</Text>{" "}
              <Text type="secondary">
                —{" "}
                {branch.manager
                  ? getEntityName(branch.manager)
                  : "No manager assigned"}{" "}
                · {branchMembers.length} member
                {branchMembers.length === 1 ? "" : "s"}
              </Text>
            </span>
          ),
          children: (
            <Table<TeamMember>
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={branchMembers}
              columns={memberColumns(branch.id)}
              locale={{ emptyText: "No members assigned to this branch yet." }}
            />
          )
        }))}
      />
    </div>
  )
}
